import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent,
} from './ui/dialog';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, Server, Package, Building2, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TYPE_META = {
  platform: { icon: Server, color: 'text-blue-600', label: 'Platform' },
  product: { icon: Package, color: 'text-emerald-600', label: 'Product' },
  vendor: { icon: Building2, color: 'text-amber-600', label: 'Vendor' },
  property: { icon: MapPin, color: 'text-purple-600', label: 'Property' },
};

export default function GlobalSearch({ open, onOpenChange, token, onNavigate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/search`, { headers, params: { q: query, limit: 15 } });
        setResults(res.data.results || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, headers]);

  useEffect(() => {
    if (open) { setQuery(''); setResults([]); }
  }, [open]);

  // Note: Cmd+K global shortcut is owned by CommandPalette; GlobalSearch opens
  // manually via the header search button + Escape closes it via Radix.

  const handleSelect = (result) => {
    onOpenChange(false);
    if (onNavigate) onNavigate(result);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="global-search-dialog">
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search platforms, products, vendors, properties..."
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
            data-testid="global-search-input"
          />
          <kbd className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono">ESC</kbd>
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {loading && (
            <div className="p-4 text-center">
              <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full mx-auto" />
            </div>
          )}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">No results found</div>
          )}
          {results.map((r, i) => {
            const meta = TYPE_META[r.type] || TYPE_META.platform;
            const Icon = meta.icon;
            return (
              <button
                key={`${r.type}-${r.id}-${i}`}
                onClick={() => handleSelect(r)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/60 transition-colors border-b last:border-b-0"
                data-testid={`search-result-${r.type}-${r.id}`}
              >
                <Icon className={`h-4 w-4 shrink-0 ${meta.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.name}</div>
                  {r.subtitle && <div className="text-xs text-muted-foreground truncate">{r.subtitle}</div>}
                </div>
                <Badge variant="outline" className="text-[9px] shrink-0">{meta.label}</Badge>
              </button>
            );
          })}
        </div>

        {!query && (
          <div className="p-4 text-center text-xs text-muted-foreground">
            Type to search across platforms, products, vendors, and properties
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
